import React, { useState, useCallback, useEffect } from "react";
import PropTypes from "prop-types";
import { labels } from "../../constants/colors";
import Autocomplete from "../../../../components/atoms/Autocomplete";
import ColorScaleOption from "./ColorScaleOption";
import {
  THEME_OPTIONS,
  THEME_LABELS,
  GESTURE_OPTS,
  GESTURE_LABELS,
  FULLSCREEN_MODE_LABELS,
  FULLSCREEN_MODE_OPTS,
  SYSTEM,
  DARK,
  SCALE_LABELS,
} from "../../constants/options";
import Btn from "../../../../components/atoms/Btn";
import Radio from "../../../../components/atoms/Radio";
import { getDefaultColorScales } from "../../models/Stage/getDefaultSettings";
import ZoomService from "../../services/zoomService/zoom.service";
import Stage from "../../models/Stage/Stage";

function Settings({
  stage,
  setOptions,
  allowedColorScales,
  allowedColorScaleCategories,
}) {
  const availableColorScales = Object.keys(labels);
  const darkCl = "themed dark";
  const lightCl = "themed light";
  const GENERAL = "general";
  const PALLETTES = "color scales";
  const tabs = allowedColorScales?.length ? [GENERAL, PALLETTES] : [GENERAL];
  const [userOptions, setUserOptions] = useState(stage.preferences$.value);
  const [tab, setTab] = useState("general");
  const className = userOptions.isDark ? darkCl : lightCl;

  const updateOptions = useCallback(
    (updated) => {
      const defOptions = stage.defaultPreferences;
      const current = { ...defOptions, ...userOptions, ...updated };
      const media = window.matchMedia("(prefers-color-scheme: dark)");
      const isDarkPreferred = media.matches;
      const isSystemDark = current.theme === SYSTEM && isDarkPreferred;
      const isDarkTheme = current.theme === DARK;
      const isDark = isDarkTheme || isSystemDark;
      const themeCl = isDark ? darkCl : lightCl;
      const options = { ...current, themeCl, isDark };
      setUserOptions(options);
    },
    [stage, userOptions, setUserOptions]
  );

  useEffect(() => {
    const themeCl = userOptions.isDark ? darkCl : lightCl;
    setOptions({ themeCl, ...userOptions });
    stage.setPreferences(userOptions);
    stage.renderAll();
  }, [stage, userOptions, setOptions]);

  const select = (x) => (e) => {
    updateOptions({ ...userOptions, [x]: e.target.value });
  };

  function calcDefaultOptions() {
    const defScales = getDefaultColorScales();

    const scalesInCats = allowedColorScaleCategories
      .filter(Boolean)
      .map(({ scales: x }) => x);

    return allowedColorScales
      .concat(...scalesInCats)
      .map((x) => ({ [x]: defScales[x] }))
      .reduce((a, i) => ({ ...a, ...i }), {});
  }

  return (
    <div className="tw-flex tw-flex-col tw-h-full tw-w-96 tw-border-l border-1">
      <div className="tw-text-xs tw-flex-grow tw-relative">
        <div className="tw-flex tw-border-b border-1">
          {tabs.map((x) => {
            const cl =
              x === tab
                ? "tw-border-primary tw-opacity-100"
                : "tw-border-transparent";
            return (
              <div className="tw-mr-2 tw-pt-6" key={x}>
                <Btn
                  key={x}
                  onSubmit={() => setTab(x)}
                  className="tw-text-sm tw-font-bold"
                >
                  <div
                    className={`tw-border-b-2 tw-pb-1 tw-opacity-60 hover:tw-opacity-100 tw-px-4 tw-capitalize ${cl}`}
                  >
                    {x}
                  </div>
                </Btn>
              </div>
            );
          })}
        </div>

        <div className="tw-p-4 tw-pt-2">
          {tab === GENERAL ? (
            <div>
              <div className="tw-h-2" />
              <div className="tw-font-bold color-2">Zoom in / out</div>
              <div className="tw-h-1" />
              <div className="">
                {GESTURE_OPTS.map((x) => (
                  <div key={x}>
                    <Radio
                      id={x}
                      name={x}
                      value={x}
                      handleSelection={select("gestureControl")}
                      isSelected={userOptions.gestureControl === x}
                    >
                      {GESTURE_LABELS[x]}
                    </Radio>
                  </div>
                ))}
              </div>
              <div className="tw-h-6" />
              <div className="tw-font-bold color-2">Theme</div>
              <div className="tw-h-1" />
              <div className="">
                {THEME_OPTIONS.map((x) => (
                  <div key={x}>
                    <Radio
                      id={x}
                      name={x}
                      value={x}
                      handleSelection={select("theme")}
                      isSelected={userOptions.theme === x}
                    >
                      {THEME_LABELS[x]}
                    </Radio>
                  </div>
                ))}
              </div>
              <div className="tw-h-6" />
              <div className="tw-font-bold color-2">Full screen mode</div>
              <div className="tw-h-1" />
              <div>
                {FULLSCREEN_MODE_OPTS.map((x) => (
                  <div key={x}>
                    <Radio
                      id={x}
                      name={x}
                      value={x}
                      handleSelection={select("fullScreenMode")}
                      isSelected={userOptions.fullScreenMode === x}
                    >
                      {FULLSCREEN_MODE_LABELS[x]}
                    </Radio>
                  </div>
                ))}
              </div>
              <div className="tw-h-4" />
              <div className="tw-font-bold color-2">Zoom speed</div>
              <div className="tw-h-1" />
              <div className="tw-flex tw-items-center tw-flex-wrap">
                {[2, 1.5, 1.25, 1, 0.5]
                  .map((x) => String(x))
                  .map((x) => (
                    <div key={x} className="tw-mr-2">
                      <Radio
                        id={x}
                        name={x}
                        value={x}
                        handleSelection={select("zoomTrackingFactor")}
                        isSelected={
                          (userOptions.zoomTrackingFactor || "1") === x
                        }
                      >
                        {`${x} x`}
                      </Radio>
                    </div>
                  ))}
              </div>
              <div className="tw-h-4" />
            </div>
          ) : null}

          {tab === PALLETTES ? (
            <div>
              {allowedColorScales.map((cat) => (
                <div key={cat} className="tw-my-1">
                  <div className="tw-flex tw-items-center">
                    <div className="tw-font-bold color-2 tw-w-32 tw-capitalize">
                      {SCALE_LABELS[cat]}
                    </div>
                    <div>
                      <Autocomplete
                        value={userOptions[cat]}
                        className="tw-w-52 tw-border border-1 tw-flex tw-px-1 tw-items-center"
                        options={availableColorScales}
                        OptionContent={(ps) => <ColorScaleOption {...ps} />}
                        OptionsWrapper={({ children }) => (
                          <div className={className}>
                            <div className="bg-1 prevent-lose-focus prevent-lose-focus color-1 tw-max-h-66 tw-absolute tw-top-0 shadow-2xl tw-rounded-lg tw-mt-2 tw-overflow-auto tw-border border-1">
                              {children}
                            </div>
                          </div>
                        )}
                        onSelect={(x) =>
                          updateOptions({
                            ...userOptions,
                            [cat]: x,
                          })
                        }
                      />
                    </div>
                  </div>
                </div>
              ))}
              {allowedColorScaleCategories.map(({ name, scales }) => (
                <div key={name} className="tw-my-3 ">
                  <div className="color-3 tw-capitalize tw-text-xs tw-font-semibold tw-mt-6 tw-opacity-70">
                    {name}
                  </div>
                  <div>
                    {scales.map((cat) => (
                      <div key={cat} className="tw-my-1">
                        <div className="tw-flex tw-items-center">
                          <div className="tw-font-bold color-2 tw-w-32 tw-capitalize">
                            {SCALE_LABELS[cat]}
                          </div>
                          <div>
                            <Autocomplete
                              value={userOptions[cat]}
                              className="tw-w-52 tw-border border-1 tw-flex tw-px-1 tw-items-center"
                              options={availableColorScales}
                              OptionContent={(ps) => (
                                <ColorScaleOption {...ps} />
                              )}
                              OptionsWrapper={({ children }) => (
                                <div className={className}>
                                  <div className="bg-1 prevent-lose-focus prevent-lose-focus color-1 tw-max-h-66 tw-absolute tw-top-0 shadow-2xl tw-rounded-lg tw-mt-2 tw-overflow-auto tw-border border-1">
                                    {children}
                                  </div>
                                </div>
                              )}
                              onSelect={(x) =>
                                updateOptions({
                                  ...userOptions,
                                  [cat]: x,
                                })
                              }
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}

              <Btn
                onSubmit={() => updateOptions(calcDefaultOptions())}
                className="tw-py-1 tw-mt-6 tw-text-primary tw-text-xs tw-underline"
              >
                Restore default color scales
              </Btn>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}

Settings.propTypes = {
  zoomService: PropTypes.instanceOf(ZoomService).isRequired,
  stage: PropTypes.instanceOf(Stage).isRequired,
  setOptions: PropTypes.func.isRequired,
  allowedColorScales: PropTypes.arrayOf(PropTypes.string),
  allowedColorScaleCategories: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      scales: PropTypes.arrayOf(PropTypes.string),
    })
  ),
  reset: PropTypes.func.isRequired,
};

Settings.defaultProps = {
  allowedColorScales: [],
  allowedColorScaleCategories: [],
};

export default Settings;
